// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE-BROWSER — enable toggle + consent affordance (ABB enable-toggle)
// ═══════════════════════════════════════════════════════════════════════════
//
// The discoverable, PERSISTED enable control for the agent bridge. It owns the
// `useAgentBridgeEnabled` preference (default-OFF, persisted) and WIRES it to the
// EXISTING `useConnectionStore` `connect` / `disconnect` lifecycle — it does NOT
// re-implement any socket / handshake / safe-contour logic (those are reused
// verbatim from connectionStore + agentBridgeMount + agentBridgeClient).
//
// SECURITY DEFAULT (the whole point of this slice — INV-OFF, ПРИОРИТЕТ #1):
//   • The enable preference persists, the pairing TOKEN does NOT (it is a bearer
//     secret; connectionStore nulls `pairingToken` on disconnect and never
//     persists it). So a reload with `enabled===true` can NEVER silently re-open
//     a headless socket — there is no token to connect with.
//   • ON MOUNT with `enabled===true`: we OPEN the ConnectDialog to re-prompt the
//     token. We do NOT call `connect` without a token.
//   • off → on transition: if the store STILL holds a `pairingToken` from earlier
//     THIS session, connect directly; otherwise OPEN the dialog so the pairing
//     step is surfaced (never bypassed).
//   • on → off transition: `disconnect()` (tears the socket down).
//
// The connect-driving lives in a `useEffect` keyed on `enabled` (NOT inside the
// hook — the hook is a pure persisted boolean). The pill markup is reused from
// the same `pill pill-connection-${status}` shape EditorHeader renders.
//
// DEP-GRAPH (ПРИОРИТЕТ #2): imports the connection store + i18n + the agentBridge
// URL const. It does NOT import editorStore — the deliberate separation between
// connection chrome and drawing state is preserved.
// ═══════════════════════════════════════════════════════════════════════════

import { useEffect, useRef } from "react";

import { AGENT_BRIDGE_WS_URL } from "../agentBridge";
import { useAgentBridgeEnabled } from "../hooks/useAgentBridgeEnabled";
import {
  type ConnectionStatus,
  useConnectionStore,
} from "../state/connectionStore";
import { useT } from "../i18n";

export interface AgentBridgeToggleProps {
  /**
   * Surface the token-pairing step: open the existing ConnectDialog. Invoked
   * when enabling needs a token we do NOT have (fresh enable with no session
   * token, or a reload with the preference ON). EditorHeader wires this to its
   * `setConnectOpen(true)` so the dialog mounts.
   */
  readonly onRequestConnect: () => void;
}

/**
 * The agent-bridge enable toggle + consent block. Drives the REAL connect /
 * disconnect via the connection store; never opens a socket without a token.
 */
export function AgentBridgeToggle({ onRequestConnect }: AgentBridgeToggleProps) {
  const t = useT();
  const { enabled, setEnabled, toggle } = useAgentBridgeEnabled();
  // Reuse the SAME selector EditorHeader uses for the status pill.
  const status = useConnectionStore((s) => s.status);

  // Track the previous `enabled` so the effect can distinguish the MOUNT pass
  // from genuine in-session transitions. `null` until the first effect run marks
  // the mount; thereafter it holds the last-seen value.
  const prevEnabledRef = useRef<boolean | null>(null);

  useEffect(() => {
    const prev = prevEnabledRef.current;
    prevEnabledRef.current = enabled;

    if (prev === null) {
      // ── MOUNT pass ──
      // With the preference ON, re-prompt the pairing token via the dialog. We
      // NEVER auto-connect on load: the token is not persisted, so a silent
      // socket is impossible — and even were a stale token present we still only
      // surface the dialog (the conservative security default). With the
      // preference OFF, do nothing.
      if (enabled) {
        onRequestConnect();
      }
      return;
    }

    if (!prev && enabled) {
      // ── off → on ──
      // Reuse a token already paired THIS session (the store keeps it until
      // disconnect / reload) — connect directly. Otherwise surface the pairing
      // dialog so the token step is NOT bypassed.
      const token = useConnectionStore.getState().pairingToken;
      if (token) {
        useConnectionStore.getState().connect(AGENT_BRIDGE_WS_URL, token);
      } else {
        onRequestConnect();
      }
      return;
    }

    if (prev && !enabled) {
      // ── on → off ── tear the socket down.
      useConnectionStore.getState().disconnect();
    }
    // `onRequestConnect` is a stable EditorHeader setter; `enabled` is the real
    // trigger. We intentionally key on `enabled` only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // ── CONSENT-GATE SAFEGUARD (INV-OFF — the toggle can NEVER render OFF while a
  // connection is active/forming) ──
  // The enable toggle is the consent gate: it must be IMPOSSIBLE to have an
  // active or forming agent-bridge connection while the persisted preference is
  // false (a connect established via ANY path — the ConnectDialog opened
  // directly, a programmatic connect, a stray code path). Whenever the live
  // `status` ENTERS a non-'disconnected' state (connecting / connected / paired /
  // error — a real connect was attempted or established) while `enabled` is still
  // false, flip the preference ON so the gate reflects the live reality.
  //
  // We key the ACTION on a status EDGE INTO non-disconnected (tracked via
  // `prevStatusRef`), NOT merely on "status is non-disconnected while enabled is
  // false". That distinction matters: the user-initiated DISABLE (handled by the
  // `enabled` effect above) calls `disconnect()`, whose store reset to
  // 'disconnected' lands on the NEXT render — so for one tick `status` is still
  // the old non-disconnected value while `enabled` has just gone false. A plain
  // "is non-disconnected" check would (wrongly) re-enable on that transient and
  // FIGHT the user's disable. An edge-INTO check does not: a disable produces a
  // non-disconnected→disconnected edge (OUT), so the safeguard ignores it; only a
  // genuine NEW connection (disconnected→connecting/…) re-enables.
  //
  // Mount case: if the very first observed status is ALREADY non-disconnected
  // while disabled (a connection that somehow predates this mount), we treat that
  // as an edge-in too and correct it. This ONLY ever SETS true — never false; the
  // user toggling OFF is the sole disable path. It does NOT interfere with the
  // reload re-prompt: on mount with `enabled===true` + no token, status stays
  // 'disconnected' until the user reconnects, so the effect stays dormant.
  const prevStatusRef = useRef<ConnectionStatus | null>(null);
  useEffect(() => {
    const prevStatus = prevStatusRef.current;
    prevStatusRef.current = status;
    // An edge INTO a non-disconnected state: either the first observed status is
    // already live (mount with a pre-existing connection), or it just left
    // 'disconnected'. A disable's stale non-disconnected→...→disconnected path is
    // an edge OUT (prevStatus was non-disconnected), so it is ignored here.
    const enteredLive =
      status !== "disconnected" &&
      (prevStatus === null || prevStatus === "disconnected");
    if (enteredLive && !enabled) {
      setEnabled(true);
    }
  }, [status, enabled, setEnabled]);

  return (
    <div className="agent-bridge-toggle" data-testid="agent-bridge-toggle">
      <label className="agent-bridge-toggle-switch">
        <input
          type="checkbox"
          role="switch"
          data-testid="agent-bridge-enable-toggle"
          checked={enabled}
          onChange={toggle}
          aria-label={t("agent_connect.toggle.aria")}
        />
        <span className="agent-bridge-toggle-label">
          {t("agent_connect.toggle.label")}
        </span>
        <span className="agent-bridge-toggle-state">
          {enabled ? t("agent_connect.toggle.on") : t("agent_connect.toggle.off")}
        </span>
      </label>

      {/* Explicit pairing affordance — opens the existing ConnectDialog so the
          user can (re-)enter the bearer token at any time. Carries the SAME
          testid the header open-button used to, so the dialog-mount path stays
          discoverable.

          CONSENT-GATE (INV-OFF): this button must NOT be able to bypass the
          enable gate. Opening the dialog from here would otherwise let the user
          paste a token + Connect → an ACTIVE bridge while the toggle still reads
          OFF. So the click ENABLES first (`setEnabled(true)` = the consent act)
          and THEN opens the dialog. When the preference was OFF, the off→on
          `enabled` effect above ALSO surfaces the dialog (no token ⇒
          onRequestConnect), so the explicit open here is idempotent — both
          resolve to `setConnectOpen(true)`, a single open with no double-open
          glitch. When the preference was already ON, `setEnabled(true)` is a
          no-op edge (no transition, the effect does not re-fire) and this
          explicit open is what surfaces the dialog. */}
      <button
        type="button"
        className="pill agent-connect-open-btn"
        data-testid="agent-connect-open"
        aria-label={t("agent_connect.open")}
        onClick={() => {
          setEnabled(true);
          onRequestConnect();
        }}
      >
        {t("agent_connect.open")}
      </button>

      {/* The live connection pill — reuses the SAME class shape EditorHeader
          renders for the header pill, so the toggle shows the connection state
          inline next to the switch. */}
      <span
        className={`pill pill-connection-${status}`}
        data-testid="agent-bridge-toggle-pill"
      >
        {t(`agent_connect.pill.${status}`)}
      </span>

      {/* When enabled but the connection terminally failed, surface the localized
          error key inline so the user sees WHY (and can re-pair via the dialog). */}
      {enabled && status === "error" ? (
        <span
          className="agent-bridge-toggle-hint"
          data-testid="agent-bridge-toggle-error-hint"
          role="status"
        >
          {t("agent_connect.status.error")}
        </span>
      ) : null}

      {/* Consent / security copy — what enabling actually grants. */}
      <div className="agent-bridge-consent" data-testid="agent-bridge-consent">
        <div className="agent-bridge-consent-title">
          {t("agent_connect.consent.title")}
        </div>
        <p className="agent-bridge-consent-body">
          {t("agent_connect.consent.body")}
        </p>
      </div>
    </div>
  );
}
